import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DownsellDataParams,
  DownsellDetailsData,
  DownsellFlowContextData,
  DownsellFlowProviderProps,
  DownsellFlowStatus,
  DownsellFlowStep,
  ErrorMessages,
  FlowStep,
  FlowTransitions,
  ProcessingStatus,
} from "./use-downsell-flow.types";
import { GetBookingDownsellResponse } from "../../adapters/get-booking-downsell";
import {
  CreateDownsellAPi,
  CreateDownsellDataParams,
} from "../../adapters/create-downsell";
import { useDownsell } from "@/hooks/use-downsell/use-downsell";

const DownsellFlowContext = createContext<DownsellFlowContextData>(
  {} as DownsellFlowContextData,
);

export function DownsellFlowProvider({ children }: DownsellFlowProviderProps) {
  const [currentStep, setCurrentStep] = useState<FlowStep>(
    DownsellFlowStep.CREATE,
  );
  const [downsellData, setDownsellData] = useState<DownsellDataParams>();
  const [downsellDetailsData, setDownsellDetailsData] =
    useState<DownsellDetailsData>();
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [downsellFlowStatus, setDownsellFlowStatus] =
    useState<DownsellFlowStatus>({
      booking: "success",
      downsell: "success",
    });
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({});
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>({
    currentOperation: "booking",
  });

  const { simulatedDownsell, handleBookingDownsellSimulation } = useDownsell();

  const navigate = useNavigate();

  const handleSubmitDownsellData = async (data: DownsellDataParams) => {
    setDownsellData(data);
    setCurrentStep(DownsellFlowStep.LOADING);

    await cancelBooking(data);
  };

  const cancelBooking = async (data: DownsellDataParams) => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      if (!downsellDetailsData) {
        handleError(new Error("Dados do downsell não carregados"), "downsell");
        setCurrentStep(DownsellFlowStep.RESULT);
        return;
      }

      if (
        ["booking-and-cycle", "booking-and-next-bookings-and-cycles"].includes(
          data.downsell_type,
        )
      ) {
        setProcessingStatus({ currentOperation: "downsell" });
      }

      const downsellSuccess = await handleCreateDownsell({
        bookingId: downsellDetailsData.booking.id,
        companyId: downsellDetailsData.company.id,
        packageId: downsellDetailsData.packageDetails.id,
        cancelStrategy: data.downsell_type,
      });

      if (!downsellSuccess) {
        setDownsellFlowStatus({ ...downsellFlowStatus, downsell: "error" });
        setCurrentStep(DownsellFlowStep.RESULT);
        return;
      }

      setDownsellFlowStatus({ ...downsellFlowStatus, downsell: "success" });
      setCurrentStep(DownsellFlowStep.RESULT);
    } catch (error) {
      handleError(error, "downsell");
      setCurrentStep(DownsellFlowStep.RESULT);
    }
  };

  const handleError = (error: unknown, type: "booking" | "downsell") => {
    const errorMessage =
      error instanceof Error ? error.message : `Erro ao criar ${type}`;
    setDownsellFlowStatus({ ...downsellFlowStatus, [type]: "error" });
    setErrorMessages({ [type]: errorMessage });
  };

  const updateDownsellFlowStatus = (status: Partial<DownsellFlowStatus>) => {
    setDownsellFlowStatus((prev) => ({ ...prev, ...status }));
  };

  const updateErrorMessages = (messages: Partial<ErrorMessages>) => {
    setErrorMessages((prev) => ({ ...prev, ...messages }));
  };

  const nextStep = () => {
    const transition = FlowTransitions[currentStep];
    setCurrentStep(transition.next);
  };

  const handleMapDownsellData = (data: GetBookingDownsellResponse) => {
    const downsellDetails: DownsellDetailsData = {
      booking: {
        id: data.booking.id,
        privateOfficeLabel: data.privateOffice.label,
        privateOfficeId: data.privateOffice.id,
        credits: data.booking.credits,
        take:
          data.booking.attributes.find(
            (attr) => attr.name === "booking_payout_rate_perc",
          )?.content || "-",
        members: data.booking.numberSeats,
      },
      company: {
        id: data.company.id,
        name: data.company.name,
      },
      cycleDetails: {
        id: data.packageVersion.id,
        startDate: data.packageVersion.startDate,
        endDate: data.packageVersion.endDate,
        credits: data.packageVersion.credits,
        exceededPrice: data.packageVersion.creditExceededPrice,
        courtesy: data.packageVersion.courtesy,
        unitPrice: data.packageVersion.creditPrice,
        packageId: data.packageVersion.packageId,
      },
      packageDetails: {
        id: data.packageDetails.id,
        name: `${data.packageDetails.id}`,
        duration: data.packageDetails.duration,
        isCompanyWide: data.packageDetails.isCompanyWide,
        products: data.packageDetails.products.split(","),
        versions: data.packageDetails.versions.map((version) => ({
          ...version,
          id: version.id,
          status: version.status,
          startDate: version.startDate,
          endDate: version.endDate,
          packageId: version.packageId,
          credits: {
            amount: version.credits,
            courtesy: 0,
            exceededPrice: 0,
            unitPrice: 0,
            consumption: {},
          },
          anticipatedCreditsReceived: 0,
          anticipatedCredits: 0,
        })),
        groups: data.packageDetails.groups.map((group) => ({
          name: group.name,
        })),
      },
    };

    setDownsellDetailsData(downsellDetails);
  };

  async function handleCreateDownsell(data: CreateDownsellDataParams) {
    if (loadingStatus) return;
    setLoadingStatus(true);

    const response = await CreateDownsellAPi({
      data,
      navigate: navigate,
    });

    setLoadingStatus(false);
    return response.success;
  }

  return (
    <DownsellFlowContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        handleSubmitDownsellData,
        nextStep,
        handleMapDownsellData,
        downsellDetailsData,
        downsellData,
        handleBookingDownsellSimulation,
        simulatedDownsell,
        handleCreateDownsell,
        loadingStatus,
        downsellFlowStatus,
        setDownsellFlowStatus: updateDownsellFlowStatus,
        errorMessages,
        setErrorMessages: updateErrorMessages,
        processingStatus,
      }}
    >
      {children}
    </DownsellFlowContext.Provider>
  );
}

export const useDownsellFlow = () => {
  const context = useContext(DownsellFlowContext);
  if (!context) {
    throw new Error(
      "useDownsellFlow must be used within a DownsellFlowProvider",
    );
  }
  return context;
};
